import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, X, Key } from '@phosphor-icons/react';
import FlagIcons from './FlagIcons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PasswordResetRequests = ({ adminId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showModal, setShowModal] = useState(null); // req
  const [newPwd, setNewPwd] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/password-reset-requests`, { params: { admin_id: adminId, status: 'pending' } });
      setRows(res.data?.requests || []);
    } catch (e) { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (adminId) load(); }, [adminId]);

  const approve = async () => {
    if (!newPwd || newPwd.length < 6) { toast.error('New password must be ≥ 6 chars'); return; }
    setBusy(showModal.id);
    try {
      const res = await axios.post(`${API}/admin/password-reset-requests/${showModal.id}/approve`, { admin_id: adminId, new_password: newPwd });
      toast.success(`Reset approved. New password: ${res.data?.new_password}`);
      setShowModal(null); setNewPwd(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
    finally { setBusy(null); }
  };

  const reject = async (req) => {
    setBusy(req.id);
    try {
      await axios.post(`${API}/admin/password-reset-requests/${req.id}/reject`, { admin_id: adminId });
      toast.success('Rejected'); load();
    } catch (e) { toast.error('Reject failed'); }
    finally { setBusy(null); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4" data-testid="pwd-reset-requests">
      <h3 className="font-bold text-slate-900 text-base mb-3">Pending Password Reset Requests</h3>
      {loading ? <div className="text-sm text-gray-500 py-4">Loading…</div>
        : rows.length === 0 ? <div className="text-sm text-gray-400 py-4">No pending requests</div>
        : (
          <div className="space-y-2">
            {rows.map(r => (
              <div key={r.id} className="p-3 border rounded-lg flex items-center justify-between" data-testid={`pwd-req-row-${r.id}`}>
                <div>
                  <div className="font-semibold text-sm">{r.username} <span className="text-xs text-gray-500">({r.role})</span></div>
                  <div className="text-xs text-gray-500">📞 {r.phone} · {r.created_at?.slice(0, 16).replace('T', ' ')}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setShowModal(r)} disabled={busy === r.id}
                    className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs font-bold flex items-center gap-1 hover:bg-emerald-700"
                    data-testid={`pwd-req-approve-${r.id}`}><Key size={14} weight="bold" /> Approve</button>
                  <button onClick={() => reject(r)} disabled={busy === r.id}
                    className="px-3 py-1.5 border border-red-500 text-red-600 rounded text-xs font-bold flex items-center gap-1 hover:bg-red-50"
                    data-testid={`pwd-req-reject-${r.id}`}><X size={14} weight="bold" /> Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowModal(null); }}>
          <div className="bg-white rounded-xl w-full max-w-sm p-4 space-y-3">
            <div className="font-bold text-sm">Reset password for <span className="text-blue-600">{showModal.username}</span></div>
            <input type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
              placeholder="New password (min 6 chars)"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="pwd-req-new-password-input" />
            <div className="flex gap-2">
              <button onClick={() => { setShowModal(null); setNewPwd(''); }} className="flex-1 py-2 border rounded text-sm">Cancel</button>
              <button onClick={approve} disabled={busy === showModal.id}
                className="flex-1 py-2 bg-emerald-600 text-white rounded text-sm font-bold disabled:opacity-50"
                data-testid="pwd-req-confirm-approve-btn"><Check size={14} weight="bold" className="inline" /> Set Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FlaggedCustomers = ({ flag = 'red' }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/users/flagged`, { params: { flag } })
      .then(res => setRows(res.data?.customers || []))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [flag]);

  const color = flag === 'red' ? '#dc2626' : '#16a34a';
  const title = flag === 'red' ? 'Suspicious' : 'Premium';

  const openChat = (c) => {
    if (c?.room_id) {
      navigate(`/admin-chat/${c.room_id}`);
    } else {
      toast.error('No active chat room for this customer');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4" data-testid={`flagged-${flag}-customers`}>
      <h3 className="font-bold text-slate-900 text-base mb-3 flex items-center gap-2">
        <FlagIcons flag={flag} size={16} />
        {flag === 'red' ? 'Suspicious' : 'Premium'} Customers ({rows.length})
      </h3>
      {loading ? <div className="text-sm text-gray-500 py-4">Loading…</div>
        : rows.length === 0 ? <div className="text-sm text-gray-400 py-4">No {flag}-flagged customers</div>
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {rows.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => openChat(c)}
                className="w-full p-3 border rounded-lg flex items-center gap-2 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors"
                style={{ borderLeftColor: color, borderLeftWidth: 4 }}
                data-testid={`flag-row-${c.id}`}
                title={c.room_id ? 'Open chat' : 'No active chat room'}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: color }}>{(c.personal_name || c.username || '?')[0].toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{c.personal_name || c.username}</div>
                  <div className="text-xs text-gray-500">{c.mb_setup || '-'} · {c.chat_type || '-'} · 📞 {c.phone}</div>
                </div>
                <span className="text-slate-400 text-lg leading-none">›</span>
              </button>
            ))}
          </div>
        )}
    </div>
  );
};

export { PasswordResetRequests, FlaggedCustomers };
